import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, getStoredToken } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import {
  Upload, Trash2, FileText, Image as ImageIcon, FileType, Loader2, Download, Video, Link2,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_ACCEPT = ".jpg,.jpeg,.png,.pdf,.doc,.docx,.ppt,.pptx";
const DEFAULT_ALLOWED_EXT = new Set(["jpg", "jpeg", "png", "pdf", "doc", "docx", "ppt", "pptx"]);
const DEFAULT_MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;

function iconFor(name = "") {
  const ext = name.split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png"].includes(ext))
    return <ImageIcon className="h-5 w-5 text-purple-600" />;
  if (ext === "pdf") return <FileText className="h-5 w-5 text-rose-600" />;
  return <FileType className="h-5 w-5 text-sky-600" />;
}

function fmtSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function downloadAttachment(att) {
  const token = getStoredToken();
  fetch(`${BACKEND}/api/attachments/${att.id}/download`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then((r) => {
      if (!r.ok) throw new Error("download failed");
      return r.blob();
    })
    .then((b) => {
      const u = window.URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u;
      a.download = att.original_filename || "archivo";
      a.target = "_top";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        a.remove();
        window.URL.revokeObjectURL(u);
      }, 2000);
    })
    .catch(() => toast.error("No se pudo descargar"));
}

export default function AttachmentsPanel({
  scope, scopeId, canEdit,
  title = "Archivos adjuntos",
  maxFiles = DEFAULT_MAX_FILES,
  accept = DEFAULT_ACCEPT,
  allowedExt = DEFAULT_ALLOWED_EXT,
  emptyLabel = "Aún no hay archivos adjuntos.",
  hint,
  allowLinks = false,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [linkMode, setLinkMode] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTitle, setLinkTitle] = useState("");
  const [addingLink, setAddingLink] = useState(false);
  const inputRef = useRef(null);

  const load = () => {
    if (!scopeId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get(`/attachments?scope=${scope}&scope_id=${scopeId}`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, scopeId]);

  const onPick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const rawFile = e.target.files?.[0];
    e.target.value = ""; // reset so picking same file again works
    if (!rawFile) return;
    const ext = (rawFile.name.split(".").pop() || "").toLowerCase();
    if (!allowedExt.has(ext)) {
      toast.error("Formato no permitido");
      return;
    }
    if (rawFile.size > MAX_BYTES) {
      toast.error("El archivo supera los 10 MB");
      return;
    }
    if (items.length >= maxFiles) {
      toast.error(`Máximo ${maxFiles} archivos`);
      return;
    }
    setUploading(true);
    const file = await compressImage(rawFile);
    const fd = new FormData();
    fd.append("scope", scope);
    fd.append("scope_id", scopeId);
    fd.append("file", file);
    try {
      const r = await api.post("/attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((xs) => [...xs, r.data]);
      toast.success("Archivo subido");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`¿Eliminar "${att.original_filename}"?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      setItems((xs) => xs.filter((x) => x.id !== att.id));
      toast.success("Archivo eliminado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addLink = async () => {
    const url = linkUrl.trim();
    if (!url) return;
    if (items.length >= maxFiles) {
      toast.error(`Máximo ${maxFiles} elementos`);
      return;
    }
    setAddingLink(true);
    try {
      const r = await api.post("/attachments/link", {
        scope, scope_id: scopeId, url, title: linkTitle.trim(),
      });
      setItems((xs) => [...xs, r.data]);
      setLinkUrl("");
      setLinkTitle("");
      setLinkMode(false);
      toast.success("Enlace añadido");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddingLink(false);
    }
  };

  const atLimit = items.length >= maxFiles;

  return (
    <div className="space-y-3" data-testid={`attachments-${scope}-${scopeId}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-bold text-slate-700">{title}</div>
          <div className="text-xs text-slate-500">
            {hint || `JPG, PNG, PDF, DOC, DOCX, PPT, PPTX · máx. 10 MB · hasta ${maxFiles} archivos`}
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              onChange={onChange}
              className="hidden"
              data-testid={`attachment-input-${scope}-${scopeId}`}
            />
            <Button
              type="button"
              size="sm"
              onClick={onPick}
              disabled={uploading || atLimit}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
              data-testid={`attachment-upload-${scope}-${scopeId}`}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Subiendo...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" /> Subir archivo
                </>
              )}
            </Button>
            {allowLinks && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setLinkMode((m) => !m)}
                disabled={atLimit}
                className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold"
                data-testid={`attachment-add-link-${scope}-${scopeId}`}
              >
                <Video className="h-4 w-4 mr-2" /> Enlace de vídeo
              </Button>
            )}
          </div>
        )}
      </div>

      {canEdit && allowLinks && linkMode && (
        <div className="border border-purple-200 bg-purple-50/50 rounded-md p-3 space-y-2" data-testid={`attachment-link-form-${scope}-${scopeId}`}>
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://youtube.com/... o cualquier enlace de vídeo"
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
            data-testid="attachment-link-url"
          />
          <input
            type="text"
            value={linkTitle}
            onChange={(e) => setLinkTitle(e.target.value)}
            placeholder="Título (opcional), ej. Resumen partido vs Canillas"
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
            data-testid="attachment-link-title"
          />
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setLinkMode(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={addLink}
              disabled={addingLink || !linkUrl.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
              data-testid="attachment-link-save"
            >
              {addingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : "Añadir enlace"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-xs text-slate-500">Cargando archivos...</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded-md p-4 text-center">
          {emptyLabel}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
          {items.map((att) => (
            <li
              key={att.id}
              className="flex items-center gap-3 p-3"
              data-testid={`attachment-row-${att.id}`}
            >
              {att.kind === "link" ? (
                <>
                  <div className="shrink-0"><Video className="h-5 w-5 text-rose-500" /></div>
                  <div className="min-w-0 flex-1">
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-semibold truncate block text-purple-700 hover:underline"
                      data-testid={`attachment-link-open-${att.id}`}
                    >
                      {att.original_filename}
                    </a>
                    <div className="text-xs text-slate-500 truncate">
                      {att.url}
                      {att.uploaded_by_name ? ` · ${att.uploaded_by_name}` : ""}
                    </div>
                  </div>
                  <a href={att.url} target="_blank" rel="noreferrer">
                    <Button type="button" size="icon" variant="ghost" title="Abrir enlace">
                      <Link2 className="h-4 w-4" />
                    </Button>
                  </a>
                </>
              ) : (
                <>
                  <div className="shrink-0">{iconFor(att.original_filename)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">
                      {att.original_filename}
                    </div>
                    <div className="text-xs text-slate-500">
                      {fmtSize(att.size)}
                      {att.uploaded_by_name ? ` · ${att.uploaded_by_name}` : ""}
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => downloadAttachment(att)}
                    title="Descargar"
                    data-testid={`attachment-download-${att.id}`}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </>
              )}
              {canEdit && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => remove(att)}
                  title="Eliminar"
                  data-testid={`attachment-delete-${att.id}`}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
